/**
 * Helper functions to handle cell references and cell values.
 */
export class ExcelCellUtils {

    // Regular expression that can test / extract absolute cell addresses of the form "sheet!cell"
    static SheetAndCellAddrRegex = /^('(?<sheetname1>[^'\\]*)'|(?<sheetname2>[^'!]+))!(?<cell>.*)$/;

    /**
     * Test for a cell's being considered 'empty'.
     * Note that this might still mean, there is a formula, which evalutes to an empty value.
     */
    static isEmptyCellValue(value: any): boolean {
        return value == null || (typeof (value) === 'string' && value.trim().length === 0);
    }

    static isEmptyCellValueAndFormula(range: Excel.Range): boolean {
        const values = range.values;
        const formulas = range.formulas;
        for (let i = 0; i < values.length; i++) {
            for (let j = 0; j < values[i].length; j++) {
                if (!ExcelCellUtils.isEmptyCellValue(values[i][j]) || !ExcelCellUtils.isEmptyCellValue(formulas[i][j])) return false;
            }
        }
        return true;
    }

    static booleanConverter(value: any): boolean {
        if (value == null) return false;
        if (typeof (value) === 'boolean') return value;
        else return value.toString().toLowerCase() === 'true';
    }

    static trimToNullConverter(value: any): string | null {
        if (ExcelCellUtils.isEmptyCellValue(value)) return null;
        else return value;
    }

    /**
     * Tests if a range address is of the form 'Sheet1!A1:B2'.
     * @param address The address to test.
     */
    static isAbsoluteRangeAddress(address: string): boolean {
        return ExcelCellUtils.SheetAndCellAddrRegex.test(address);
    }

    static parseRangeAddress(address: string): ParsedCellAddress| null {
        const matchResult = ExcelCellUtils.SheetAndCellAddrRegex.exec(address) as any;
        if (matchResult === null) return null;

        const { groups: { sheetname1, sheetname2, cell } } = matchResult;
        const sheetName = (sheetname1 ?? sheetname2 ?? '').trim();
        const cellAddress = cell.trim();
        if (sheetName === '') throw new Error(`missing sheet name in range address ${address}`);
        if (cellAddress === '') throw new Error(`missing cell address in range address ${address}`);
        return { sheetName, cellAddress };
    }

    static getAddress(range: Excel.Range): ParsedCellAddress {
        return ExcelCellUtils.parseRangeAddress(range.address)!;
    }

    /** Borrowed from https://stackoverflow.com/questions/21229180/convert-column-index-into-corresponding-column-letter . */
    static columnToLetter(column: number): string {
        let temp;
        let letter = '';
        while (column > 0) {
            temp = column % 26;
            letter = String.fromCharCode(temp + 65) + letter;
            column = (column - temp) / 26;
        }
        return letter;
    }

    /**
     * Returns the union range of the given ranges.
     */
    static getUnion(ranges: Excel.Range[]): Excel.Range {
        if (ranges.length === 0) throw new Error('Cannot get union of empty range list.');
        return ranges.reduce((a, b) => a.getBoundingRect(b));
    }

    isValidExcelAddress(address: string | null | undefined): boolean {
        // empty addresses are not valid
        if (address == null || address.trim().length === 0) return false;

        const validRangeRegex = /^([A-Za-z])\1*(?:[1-9]\d*)*:([A-Za-z])\2*(?:[1-9]\d*)*$|^(?:([A-Za-z])\3*|[1-9]\d*:)[1-9]\d*$/
        return true;
    }

}

/** Value object that represents a parsed cell address. */
export interface ParsedCellAddress {
    sheetName: string;
    cellAddress: string;
}
